import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Download, QrCode, Smartphone, Palette, Settings, Wifi, Mail, Phone } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input, Textarea } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';
import QRCodeLib from 'qrcode';

// This will be moved inside the component to access t function

export const QrGeneratorTool: React.FC = () => {
  const { t } = useTranslation();

  const qrTypes = [
    { id: 'text', name: t('tools.qrGenerator.plainText'), icon: QrCode },
    { id: 'url', name: t('tools.qrGenerator.websiteUrl'), icon: QrCode },
    { id: 'wifi', name: t('tools.qrGenerator.wifiNetwork'), icon: Wifi },
    { id: 'email', name: t('tools.qrGenerator.email'), icon: Mail },
    { id: 'phone', name: t('tools.qrGenerator.phoneNumber'), icon: Phone },
    { id: 'sms', name: t('tools.qrGenerator.smsMessage'), icon: Smartphone },
  ];

  const [text, setText] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [size, setSize] = useState(256);
  const [qrType, setQrType] = useState('text');
  const [errorCorrection, setErrorCorrection] = useState('M');
  const [foregroundColor, setForegroundColor] = useState('#000000');
  const [backgroundColor, setBackgroundColor] = useState('#FFFFFF');

  // WiFi specific fields
  const [wifiSSID, setWifiSSID] = useState('');
  const [wifiPassword, setWifiPassword] = useState('');
  const [wifiSecurity, setWifiSecurity] = useState('WPA');
  const [wifiHidden, setWifiHidden] = useState(false);

  // Email specific fields
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');

  // Phone/SMS specific fields
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsMessage, setSmsMessage] = useState('');

  const generateQrData = () => {
    switch (qrType) {
      case 'text':
        return text;
      case 'url':
        return text.startsWith('http') ? text : `https://${text}`;
      case 'wifi':
        return `WIFI:T:${wifiSecurity};S:${wifiSSID};P:${wifiPassword};H:${wifiHidden ? 'true' : 'false'};;`;
      case 'email':
        return `mailto:${emailTo}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
      case 'phone':
        return `tel:${phoneNumber}`;
      case 'sms':
        return `sms:${phoneNumber}?body=${encodeURIComponent(smsMessage)}`;
      default:
        return text;
    }
  };
  const [errorLevel, setErrorLevel] = useState<'L' | 'M' | 'Q' | 'H'>('M');

  useEffect(() => {
    if (text) {
      generateQRCode();
    } else {
      setQrCodeUrl('');
    }
  }, [text, size, errorLevel]);

  const generateQRCode = async () => {
    try {
      const qrData = generateQrData();
      const url = await QRCodeLib.toDataURL(qrData, {
        width: size,
        errorCorrectionLevel: errorLevel,
        color: {
          dark: foregroundColor,
          light: backgroundColor
        }
      });
      setQrCodeUrl(url);
    } catch (err) {
      console.error(t('tools.qrGenerator.errorGenerating'), err);
      setQrCodeUrl('');
    }
  };

  const downloadQRCode = () => {
    if (qrCodeUrl) {
      const link = document.createElement('a');
      link.download = 'qrcode.png';
      link.href = qrCodeUrl;
      link.click();
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.failedToCopyToClipboard'));
    }
  };

  const presetTexts = [
    'https://example.com',
    'Hello, World!',
    'mailto:contact@example.com',
    'tel:+1234567890',
    'WIFI:T:WPA;S:MyNetwork;P:password123;;',
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <QrCode className="w-5 h-5" />
            <span>{t('tools.qrGenerator.name')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* QR Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('tools.qrGenerator.qrCodeType')}
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {qrTypes.map((type) => (
                  <Button
                    key={type.id}
                    onClick={() => setQrType(type.id)}
                    variant={qrType === type.id ? 'primary' : 'outline'}
                    size="sm"
                    className="flex items-center space-x-2"
                  >
                    <type.icon className="w-4 h-4" />
                    <span>{type.name}</span>
                  </Button>
                ))}
              </div>
            </div>

            {/* Dynamic Input Based on Type */}
            {qrType === 'text' && (
              <Textarea
                label="Text Content"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Enter text to encode..."
                className="min-h-[100px]"
              />
            )}

            {qrType === 'url' && (
              <Input
                label="Website URL"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="https://example.com"
              />
            )}

            {qrType === 'wifi' && (
              <div className="space-y-3">
                <Input
                  label="Network Name (SSID)"
                  value={wifiSSID}
                  onChange={(e) => setWifiSSID(e.target.value)}
                  placeholder="MyWiFiNetwork"
                />
                <Input
                  label="Password"
                  type="password"
                  value={wifiPassword}
                  onChange={(e) => setWifiPassword(e.target.value)}
                  placeholder="password123"
                />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Security Type
                    </label>
                    <select
                      value={wifiSecurity}
                      onChange={(e) => setWifiSecurity(e.target.value)}
                      className="input"
                    >
                      <option value="WPA">WPA/WPA2</option>
                      <option value="WEP">WEP</option>
                      <option value="nopass">No Password</option>
                    </select>
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={wifiHidden}
                        onChange={(e) => setWifiHidden(e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm">Hidden Network</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {qrType === 'email' && (
              <div className="space-y-3">
                <Input
                  label="Email Address"
                  type="email"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder="contact@example.com"
                />
                <Input
                  label="Subject"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Email subject"
                />
                <Textarea
                  label="Message"
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  placeholder="Email message..."
                  className="min-h-[80px]"
                />
              </div>
            )}

            {qrType === 'phone' && (
              <Input
                label="Phone Number"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+1234567890"
              />
            )}

            {qrType === 'sms' && (
              <div className="space-y-3">
                <Input
                  label="Phone Number"
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="+1234567890"
                />
                <Textarea
                  label="Message"
                  value={smsMessage}
                  onChange={(e) => setSmsMessage(e.target.value)}
                  placeholder="SMS message..."
                  className="min-h-[80px]"
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={t('tools.qrGenerator.size')}
                type="number"
                min="128"
                max="1024"
                step="32"
                value={size}
                onChange={(e) => setSize(parseInt(e.target.value) || 256)}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('tools.qrGenerator.errorCorrection')}
                </label>
                <select
                  value={errorLevel}
                  onChange={(e) => setErrorLevel(e.target.value as 'L' | 'M' | 'Q' | 'H')}
                  className="input"
                >
                  <option value="L">{t('tools.qrGenerator.errorLow')}</option>
                  <option value="M">{t('tools.qrGenerator.errorMedium')}</option>
                  <option value="Q">{t('tools.qrGenerator.errorQuartile')}</option>
                  <option value="H">{t('tools.qrGenerator.errorHigh')}</option>
                </select>
              </div>
            </div>

            {/* Color Customization */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Palette className="w-4 h-4 inline mr-2" />
                Color Customization
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Foreground Color
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={foregroundColor}
                      onChange={(e) => setForegroundColor(e.target.value)}
                      className="w-12 h-8 rounded border border-gray-300 cursor-pointer"
                    />
                    <Input
                      value={foregroundColor}
                      onChange={(e) => setForegroundColor(e.target.value)}
                      className="font-mono text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Background Color
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={backgroundColor}
                      onChange={(e) => setBackgroundColor(e.target.value)}
                      className="w-12 h-8 rounded border border-gray-300 cursor-pointer"
                    />
                    <Input
                      value={backgroundColor}
                      onChange={(e) => setBackgroundColor(e.target.value)}
                      className="font-mono text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Quick Examples:
              </label>
              <div className="flex flex-wrap gap-2">
                {presetTexts.map((preset, index) => (
                  <Button
                    key={index}
                    onClick={() => setText(preset)}
                    variant="outline"
                    size="sm"
                  >
                    {preset.length > 20 ? `${preset.substring(0, 20)}...` : preset}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {qrCodeUrl && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{t('tools.qrGenerator.generatedQrCode')}</span>
              <div className="flex gap-2">
                <Button
                  onClick={() => copyToClipboard(text)}
                  size="sm"
                  variant="outline"
                >
                  <Copy className="w-3 h-3 mr-1" />
                  {t('tools.qrGenerator.copyText')}
                </Button>
                <Button
                  onClick={downloadQRCode}
                  size="sm"
                  variant="outline"
                >
                  <Download className="w-3 h-3 mr-1" />
                  {t('common.download')}
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center space-y-4">
              <img
                src={qrCodeUrl}
                alt={t('tools.qrGenerator.generatedQrCodeAlt')}
                className="border border-gray-300 dark:border-gray-600 rounded"
                style={{ width: size, height: size }}
              />
              <div className="text-sm text-gray-600 dark:text-gray-400 text-center">
                <div>{t('tools.qrGenerator.size')}: {size}x{size} {t('tools.qrGenerator.pixels')}</div>
                <div>{t('tools.qrGenerator.errorCorrection')}: {errorLevel}</div>
                <div>{t('tools.qrGenerator.dataLength')}: {text.length} {t('tools.qrGenerator.characters')}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
